import React, { useEffect, useMemo, useState, Fragment } from 'react';
/**
 * NumberSequenceRenderer — Fill missing values in a number sequence.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function NumberSequenceRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const sequence: Array<string | number | null | undefined> = config.sequence || [];
  // Support both plural missingIndices array AND singular missingIndex (legacy seeds)
  const missingIndices: number[] = config.missingIndices ? config.missingIndices : config.missingIndex !== undefined ? [config.missingIndex] : [];
  // Auto-detect missing indices from null/undefined/"?" values if not provided
  const effectiveMissingIndices = missingIndices.length > 0 ? missingIndices : sequence.reduce<number[]>((acc, val, idx) => {
    if (val === null || val === undefined || val === '?') acc.push(idx);
    return acc;
  }, []);
  const [inputs, setInputs] = useState<Record<number, string>>({});
  const [isFinePointer, setIsFinePointer] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      setIsFinePointer(window.matchMedia('(pointer: fine)').matches);
    }
  }, []);
  useEffect(() => {
    setInputs({});
  }, [question.id]);
  useEffect(() => {
    const isReady = effectiveMissingIndices.every(idx => inputs[idx] && inputs[idx].trim() !== '');
    onReadyChange?.(isReady);
  }, [inputs, effectiveMissingIndices, onReadyChange]);
  const answerPayload = useMemo(() => effectiveMissingIndices.map(idx => inputs[idx] || '').join(','), [effectiveMissingIndices, inputs]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // Build render items
  const maxIndex = Math.max(sequence.length - 1, effectiveMissingIndices.length > 0 ? Math.max(...effectiveMissingIndices) : -1) + 1;
  const items: Array<{
    type: 'static';
    value: string | number;
  } | {
    type: 'input';
    index: number;
  }> = [];
  for (let i = 0; i < maxIndex; i++) {
    if (effectiveMissingIndices.includes(i)) {
      items.push({
        type: 'input',
        index: i
      });
    } else if (sequence[i] !== undefined && sequence[i] !== null) {
      items.push({
        type: 'static',
        value: sequence[i]!
      });
    }
  }
  // Detect money/time values for keyboard mode
  const sampleValue = sequence.find(v => v !== null && v !== undefined);
  const isMoneyOrTime = sampleValue && (String(sampleValue).includes('€') || String(sampleValue).includes(':'));
  // INTERACTIVE-5.3: autofocus the first empty input — only on devices with a
  // fine pointer (desktop). On touch devices autoFocus triggers a keyboard
  // pop-up that scrolls the sequence off-screen.
  const firstEmptyIndex = effectiveMissingIndices.find(idx => !inputs[idx] || inputs[idx].trim() === '');
  return <div className="w-full max-w-4xl mx-auto" data-id="element-11835" data-mp="37l2542yxr4ii">
      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4" data-id="element-11836" data-mp="37j7fwcgmsq8m">
        {items.map((item, i) => <Fragment key={i} data-id="element-11837" data-mp="2fu4ed4g4ptzo" data-mpi={i}>
            {item.type === 'static' ? <div className="min-w-[80px] md:min-w-[100px] h-16 md:h-20 flex items-center justify-center bg-white border-2 border-slate-200 rounded-2xl text-lg md:text-xl font-bold text-slate-700 shadow-sm px-3" data-id="element-11838" data-mp="2fvyy4ffnzg26">
                <LatexRenderer content={String(item.value)} data-id="element-11839" data-mp="2oszjed7fsy1e" />
              </div> : <input type="text" inputMode={isMoneyOrTime ? 'text' : 'decimal'} value={inputs[item.index] || ''} onChange={e => {
          let val = e.target.value;
          // R7-DID2: Filter non-numeric characters for numeric sequences
          if (!isMoneyOrTime) {
            val = val.replace(/[^0-9.,-]/g, '');
          }
          setInputs(prev => ({
            ...prev,
            [item.index]: val
          }));
        }} className="min-w-[80px] md:min-w-[100px] h-16 md:h-20 text-center bg-white border-2 border-indigo-300 rounded-2xl text-lg md:text-xl font-bold text-indigo-700 shadow-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-indigo-200 px-2" placeholder="?" aria-label={`Ontbrekend getal ${i + 1}`} autoFocus={isFinePointer && item.index === firstEmptyIndex} data-id="element-11840" data-mp="212vfe4qc6w9v" />}
            {i < items.length - 1 && <div className="text-slate-300 flex-shrink-0" data-id="element-11841" data-mp="3qdeeehkq0m8h">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 md:w-6 md:h-6" data-id="element-11842" data-mp="3qez9q9ev097e">
                  <line x1="5" y1="12" x2="19" y2="12" data-id="element-11843" data-mp="d5t3ti80ymwj" />
                  <polyline points="12 5 19 12 12 19" data-id="element-11844" data-mp="950wwvmmku3f" />
                </svg>
              </div>}
          </Fragment>)}
      </div>
      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-11845" data-mp="443fwmdcq63i">
        Vul de ontbrekende getallen in
      </p>
    </div>;
}